import { UpsertAdjustmentModal } from "@/pages/adjustments/manager/components/UpsertAdjustmentModal";
import { routePartsManager } from "@/setup/routes/report.routes";
import { backgroundColor } from "@/utils/theme";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	Build,
	CheckCircle,
	Close,
	Comment,
	Visibility,
} from "@mui/icons-material";
import {
	Badge,
	Box,
	Card,
	CardActions,
	CardContent,
	Divider,
	Grid2,
	IconButton,
	Theme,
	Tooltip,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { StandData } from "../models/StandData";
import { CommentModal } from "./modals/CommentModal";
import { InstallUninstallModal } from "./modals/InstallUninstallModal";
import { SparesModal } from "./modals/SparesModal";

const moment = getMomentTz();

const badgeContentStyle = {
	right: 3,
	top: 5,
	border: (theme: Theme) => `2px solid ${theme.palette.background.paper}`,
	padding: "0 4px",
	fontSize: "0.7rem",
	height: "16px",
	minWidth: "16px",
	backgroundColor: "#4050B5",
	color: "white",
};

type Props = {
	stand: StandData;
	selectedStand?: StandData | null;
	setSelectedStand: (stand: StandData | null) => void;
	standInstalleds?: String[];
	isLoading?: boolean;
	currentCampaignSectionId: number | null;
};

export const StandCard = ({
	stand,
	selectedStand,
	setSelectedStand,
	standInstalleds,
	isLoading = false,
	currentCampaignSectionId,
}: Props) => {
	const queryClient = useQueryClient();

	const [showAdjustmentModal, setShowAdjustmentModal] = useState<
		"new" | "edit" | "delete" | ""
	>("");
	const [showCommentsModal, setShowCommentsModal] = useState(false);
	const [showInstallUninstallModal, setShowInstallUninstallModal] =
		useState(false);
	const [showSparesModal, setShowSparesModal] = useState(false);

	const cardBackgroundColor =
		selectedStand?.StandId === stand.StandId ? "#B5D3EF" : undefined;

	return (
		<>
			<Grid2
				size={{ md: 12 / 7 }}
				display="flex"
				onContextMenu={(e) => {
					setSelectedStand(stand);
					e.preventDefault();
				}}
			>
				<Card
					sx={{
						display: "flex",
						flexDirection: "column",
						height: "100%",
						width: "100%",
						boxShadow: "4px 4px 10px rgba(0, 0, 0, 0.1)",
						"&:hover": {
							backgroundColor: `${backgroundColor} !important`,
						},
						backgroundColor: cardBackgroundColor,
					}}
				>
					<Box
						className="card-header"
						sx={{
							backgroundColor:
								stand.CarrierStatusCode === "I"
									? "rgba(51, 69, 95, 1)"
									: undefined,
							px: 3,
							py: 1,
							transition: "background-color 0.2s",
						}}
						flexDirection="column"
					>
						<Typography
							variant="h5"
							component="h3"
							fontWeight={600}
							textAlign={"center"}
							color={stand.CarrierStatusCode === "I" ? "white" : undefined}
						>
							{stand.StandName}
						</Typography>
						<Typography
							variant="body2"
							fontWeight={500}
							textAlign="center"
							color={stand.CarrierStatusCode === "I" ? "white" : undefined}
						>
							{stand.PartName ?? "-"}
						</Typography>
					</Box>
					<CardContent
						sx={{
							px: (theme) => theme.spacing(3),
							pb: 0,
							flexGrow: 1,
						}}
					>
						<Grid2 container spacing={2} mb={1.5} minHeight={50}>
							<Grid2 size={{ md: 6 }}>
								<Typography
									variant="caption"
									display="block"
									fontSize="0.7rem"
									marginBottom={(theme) => theme.spacing(0.5)}
									lineHeight={1}
								>
									Top Roll
								</Typography>
								<Tooltip
									title={stand.TopRollName ? "View in Parts Manager" : ""}
								>
									<Typography
										variant="body1"
										display="block"
										fontWeight={600}
										fontSize={"1rem"}
										lineHeight={1}
										color={stand.TopRollId ? "#1976d2" : undefined}
										style={{
											textDecoration: "none",
											cursor: stand.TopRollId ? "pointer" : "default",
										}}
										onClick={() => {
											window.open(
												`${routePartsManager}/${stand?.TopRollId}`,
												"_blank",
											);
										}}
									>
										{stand.TopRollName ?? "-"}
									</Typography>
								</Tooltip>
							</Grid2>

							<Grid2 size={{ md: 6 }}>
								<Typography
									variant="caption"
									display="block"
									fontSize="0.7rem"
									marginBottom={(theme) => theme.spacing(0.5)}
									lineHeight={1}
									style={{}}
								>
									Bot Roll
								</Typography>
								<Tooltip
									title={stand.BottomRollName ? "View in Parts Manager" : ""}
								>
									<Typography
										variant="body1"
										display="block"
										fontWeight={600}
										fontSize={"1rem"}
										lineHeight={1}
										color={stand.TopRollId ? "#1976d2" : undefined}
										style={{
											textDecoration: "none",
											cursor: stand.TopRollId ? "pointer" : "default",
										}}
										onClick={() => {
											window.open(
												`${routePartsManager}/${stand?.BottomRollId}`,
												"_blank",
											);
										}}
									>
										{stand.BottomRollName ?? "-"}
									</Typography>
								</Tooltip>
							</Grid2>
						</Grid2>

						<Grid2 container spacing={2} mb={1.5}>
							<Grid2 size={{ md: 6 }}>
								<Typography
									variant="caption"
									display="block"
									fontSize="0.7rem"
									marginBottom={(theme) => theme.spacing(0.5)}
									lineHeight={1}
									style={{}}
								>
									Acc Tons
								</Typography>
								<Typography
									variant="body1"
									display="block"
									fontWeight={600}
									fontSize={"1rem"}
									lineHeight={1}
								>
									{stand.TopRollStartWeight && stand.TopRollCampaignWeight
										? (
												stand.TopRollStartWeight + stand.TopRollCampaignWeight
											).toFixed(2)
										: "-"}
								</Typography>
							</Grid2>

							<Grid2 size={{ md: 6 }}>
								<Typography
									variant="caption"
									display="block"
									fontSize="0.7rem"
									marginBottom={(theme) => theme.spacing(0.5)}
									lineHeight={1}
									style={{}}
								>
									Acc Tons
								</Typography>
								<Typography
									variant="body1"
									display="block"
									fontWeight={600}
									fontSize={"1rem"}
									lineHeight={1}
								>
									{stand.BottomRollStartWeight && stand.BottomRollCampaignWeight
										? (
												stand.BottomRollStartWeight +
												stand.BottomRollCampaignWeight
											).toFixed(2)
										: "-"}
								</Typography>
							</Grid2>
						</Grid2>

						<Grid2 container spacing={2} mb={1.5}>
							<Grid2 size={{ md: 6 }}>
								<Typography
									variant="caption"
									display="block"
									fontSize="0.7rem"
									marginBottom={(theme) => theme.spacing(0.5)}
									lineHeight={1}
									style={{}}
								>
									Since Install
								</Typography>
								<Typography
									variant="body1"
									display="block"
									fontWeight={600}
									fontSize={"1rem"}
									lineHeight={1}
								>
									{stand.CarrierCampaignWeight
										? stand.CarrierCampaignWeight.toFixed(2)
										: "-"}
								</Typography>
							</Grid2>

							<Grid2 size={{ md: 6 }}>
								<Typography
									variant="caption"
									display="block"
									fontSize="0.7rem"
									marginBottom={(theme) => theme.spacing(0.5)}
									lineHeight={1}
									style={{}}
								>
									Diameter
								</Typography>
								<Typography
									variant="body1"
									display="block"
									fontWeight={600}
									fontSize={"1rem"}
									lineHeight={1}
								>
									{stand.TopRollDiameter?.toFixed(3) ?? "-"}
								</Typography>
							</Grid2>
						</Grid2>

						<Grid2 container spacing={2} mb={1.5}>
							<Grid2 size={{ xs: 12 }}>
								<Typography
									variant="caption"
									display="block"
									fontSize="0.7rem"
									marginBottom={(theme) => theme.spacing(0.5)}
									lineHeight={1}
									style={{}}
								>
									Installed At
								</Typography>
								<Typography
									variant="body1"
									display="block"
									fontWeight={600}
									fontSize={"1rem"}
									lineHeight={1}
								>
									{stand.InstalledAt
										? moment(stand.InstalledAt).format("MM/DD/YY HH:mm:ss")
										: "-"}
								</Typography>
							</Grid2>
						</Grid2>
					</CardContent>
					<CardActions>
						<Grid2
							container
							spacing={1}
							direction="row"
							style={{
								display: "flex",
								padding: "0 10px",
							}}
						>
							<Grid2 size={{ md: 12 }}>
								<Divider
									style={{
										backgroundColor:
											stand.CarrierStatusCode === "I" ? "#6c6c6cff" : undefined,
									}}
								/>
							</Grid2>
							<Grid2 size={{ xs: 3 }}>
								<Tooltip title="New Adjustment">
									<Badge
										badgeContent={stand.AdjustmentsSinceInstallation}
										sx={{
											"& .MuiBadge-badge":
												stand.AdjustmentsSinceInstallation > 0
													? badgeContentStyle
													: {
															display: "none",
														},
										}}
										max={99}
									>
										<IconButton
											id={`stand-${stand.StandName}-adjust`}
											size="small"
											color="primary"
											onClick={() => {
												setSelectedStand(stand);
												setShowAdjustmentModal("new");
											}}
											style={{}}
											disabled={stand.CarrierStatusCode !== "I"}
										>
											<Build fontSize="small" />
										</IconButton>
									</Badge>
								</Tooltip>
							</Grid2>
							<Grid2 size={{ xs: 3 }}>
								<Tooltip
									title={stand.Comments ? `"${stand.Comments}"` : "Add Comment"}
								>
									<Badge
										badgeContent={stand.Comments ? " " : 0}
										sx={{
											"& .MuiBadge-badge": stand.Comments
												? {
														right: 3,
														top: 5,
														border: (theme) =>
															`2px solid ${theme.palette.background.paper}`,
														padding: "0 4px",
														fontSize: "0.7rem",
														height: "16px",
														minWidth: "16px",
														backgroundColor: "#4050B5",
														color: "white",
													}
												: {
														display: "none",
													},
										}}
									>
										<IconButton
											id={`stand-${stand.StandName}-comment`}
											size="small"
											color="primary"
											style={{}}
											onClick={() => {
												setSelectedStand(stand);
												setShowCommentsModal(true);
											}}
										>
											<Comment fontSize="small" />
										</IconButton>
									</Badge>
								</Tooltip>
							</Grid2>
							<Grid2 size={{ xs: 3 }}>
								<Tooltip
									title={
										(stand.CarrierStatusCode === "I"
											? "Uninstall"
											: "Install") + " carrier"
									}
								>
									<Badge
										badgeContent={stand.CarriersToInstallCount}
										sx={{
											"& .MuiBadge-badge":
												stand.CarriersToInstallCount > 0 &&
												stand.CarrierStatusCode !== "I"
													? badgeContentStyle
													: {
															display: "none",
														},
										}}
										max={99}
									>
										<IconButton
											id={`stand-${stand.StandName}-${
												stand.CarrierStatusCode === "I"
													? "uninstall"
													: "install"
											}`}
											size="small"
											color="primary"
											onClick={() => {
												setSelectedStand(stand);
												setShowInstallUninstallModal(true);
											}}
											style={{}}
										>
											{stand.CarrierStatusCode === "I" ? (
												<Close fontSize="small" />
											) : (
												<CheckCircle fontSize="small" />
											)}
										</IconButton>
									</Badge>
								</Tooltip>
							</Grid2>
							<Grid2 size={{ xs: 3 }}>
								<Tooltip
									title={
										stand.SpareCount == 0
											? "No Spares"
											: `View Spares (${stand.SpareCount})`
									}
								>
									<Badge
										badgeContent={stand.SpareCount}
										sx={{
											"& .MuiBadge-badge":
												stand.SpareCount > 0
													? badgeContentStyle
													: {
															display: "none",
														},
										}}
										max={99}
									>
										<IconButton
											id={`stand-${stand.StandName}-spares`}
											size="small"
											color={stand.SpareCount == 0 ? "error" : "primary"}
											onClick={
												stand.SpareCount == 0
													? () => {}
													: () => {
															setSelectedStand(stand);
															setShowSparesModal(true);
														}
											}
										>
											<Visibility fontSize="small" />
										</IconButton>
									</Badge>
								</Tooltip>
							</Grid2>
						</Grid2>
					</CardActions>
				</Card>
			</Grid2>
			<UpsertAdjustmentModal
				showModal={showAdjustmentModal}
				onHide={async (update) => {
					setShowAdjustmentModal("");
					setSelectedStand(null);
					if (update) {
						await queryClient.invalidateQueries(["millOverviews"]);
					}
				}}
				millAdjustmentId={null}
				initialStands={[
					{
						StandId: stand.StandId,
						StandName: stand.StandName,
					},
				]}
				suffixTitle="Mill Overview"
				limitSelectableStands={standInstalleds}
			/>
			<CommentModal
				show={showCommentsModal}
				onHide={() => {
					setShowCommentsModal(false);
					setSelectedStand(null);
				}}
				standId={stand.StandId}
				currentComments={stand.Comments ?? ""}
			/>
			<InstallUninstallModal
				show={showInstallUninstallModal}
				onHide={() => {
					setShowInstallUninstallModal(false);
					setSelectedStand(null);
				}}
				standId={stand.StandId}
				standName={stand.StandName}
			/>
			<SparesModal
				show={showSparesModal}
				onHide={(success) => {
					setShowSparesModal(false);
					setSelectedStand(null);
				}}
				standId={stand.StandId}
				standName={stand.StandName}
				currentCampaignSectionId={currentCampaignSectionId}
			/>
		</>
	);
};
